import { useState, useEffect } from 'react';
import ColumnDefinition from '../../../../../pieces/grids/columnGenerator';
import * as allConstants from '../../../../../constants/allConstants';
import {INSPECTION_REQUIREMENTS} from '../../../../../constants/options/filterOptions/columnFilterOptions';
import Utils from '../../../../../utils/utils';
import {AI_CALL_RESULTS} from '../../../../../constants/options/aiCallResult';
import {getStatusColumns} from '../../../helpers/jobGridOptions';
import {isContractor} from '../../../../../permissions/utils';
//import RequirementFilter from '../../../../../pieces/grids/requirementFilter';

const inspectionApiTypes = {
  all: 'all',
  previous: 'previous',
  pending: 'pending',
  manual: 'manual',
  completed: 'completed',
  hold: 'hold',
  failed: 'failed',
  futureDate: 'future date',
  customerDeclined: 'customer declined',
  report: 'report',
  aiAll: 'ai all',
  aiReady: 'ai ready',
  aiInProgress: 'ai in progress',
  aiRescheduled: 'ai rescheduled',
  aiNeedToVerify: 'ai need to verify',
  aiClosedOut: 'ai closed out',
};

const AI_RESULT_DISPLAY_BY_KEY = (() => {
  const m = new Map();
  for (const r of Object.values(AI_CALL_RESULTS)) {
    m.set(r.api_name.toLowerCase(), r.display);
    m.set(r.value.toLowerCase(), r.display);
  }
  return m;
})();

const formatAiResultCell = (raw) => {
  if (raw == null || raw === '') {
    return null;
  }
  const s = String(raw).trim();
  const mapped = AI_RESULT_DISPLAY_BY_KEY.get(s.toLowerCase());
  return mapped != null ? mapped : s;
};

const AI_CALL_PRIORITY_DISPLAY_BY_VALUE = (() => {
  const m = new Map();
  for (const p of Object.values(allConstants.AI_CALL_PRIORITIES)) {
    m.set(String(p.value), p.display);
  }
  return m;
})();

const AI_CALL_PRIORITY_DISPLAY_TO_VALUE = (() => {
  const m = new Map();
  for (const p of Object.values(allConstants.AI_CALL_PRIORITIES)) {
    const num = Number(p.value);
    m.set(p.display, num);
    m.set(p.display.toLowerCase(), num);
    m.set(String(p.value), num);
    m.set(num, num);
  }
  return m;
})();

const formatAiCallPriorityCell = (raw) => {
  if (raw == null || raw === '') {
    return allConstants.AI_CALL_PRIORITIES.NORMAL.display;
  }
  const s = String(raw).trim();
  return AI_CALL_PRIORITY_DISPLAY_BY_VALUE.get(s) ?? s;
};

const convertAiCallPriorityFilterToApi = (values) =>
  values.map((v) => {
    if (v == null || v === '') {
      return v;
    }
    const key = typeof v === 'string' ? v.trim() : v;
    const mapped = AI_CALL_PRIORITY_DISPLAY_TO_VALUE.get(key)
      ?? AI_CALL_PRIORITY_DISPLAY_TO_VALUE.get(String(key).toLowerCase());
    if (mapped != null && !Number.isNaN(mapped)) {
      return mapped;
    }
    const num = Number(key);
    return Number.isNaN(num) ? key : num;
  });

const getInspectionColumns = (handleDisplayJobDetails, cellRendererName, jobOptions, userRole, tagNameList = [], userCompanies = [], impersonating = false, impersonateUserId = null) => {
 
  // console.log("inside getInspectionColumns");
  // console.log("jobOptions => ", jobOptions);
  // console.log("user role 2=> ", userRole);
  // console.log("tagNameList => ", tagNameList);

  const JOB_FIELDS = allConstants.JOB_FIELDS;
  const INSP_FIELDS = allConstants.INSPECTION_FIELDS;
  const SCHEDULING_FIELDS = allConstants.SCHEDULING_FIELDS;

  const inspectionStatusFilterValues = (() => {
    const vals = Object.values(allConstants.INSPECTION_STATUSES).map((s) => s.value);
    if (!isContractor(userRole) && !impersonating && !impersonateUserId) {
      return vals;
    }
    const exclude = new Set([
      allConstants.INSPECTION_STATUSES.AI_READY.value,
      allConstants.INSPECTION_STATUSES.AI_IN_PROGRESS.value,
      allConstants.INSPECTION_STATUSES.AI_VERIFY.value,
      allConstants.INSPECTION_STATUSES.AI_CLOSED_OUT.value,
    ]);
    return vals.filter((v) => !exclude.has(v));
  })();

  return {
    id: new ColumnDefinition({field: JOB_FIELDS.ID, hide: true}),
    company: new ColumnDefinition({
      field: allConstants.COMPANY_FIELDS.ID,
      parent: 'company',
      hide: true,
    }),
    companyName: new ColumnDefinition({
      field: allConstants.COMPANY_FIELDS.NAME,
      sortable: true,
      parent: 'company',
      hide: false,
      filter: 'agSetColumnFilter',
      filterValues: userCompanies,
    }),
    jobNumber: new ColumnDefinition({
      field: JOB_FIELDS.JOB_NUMBER,
      sortable: true,
      lockPinned: true,
      filter: 'agNumberColumnFilter',
      pinned: 'left',
      checkboxSelection: true,
      cellRenderer: cellRendererName,
      cellRendererParams: (params) => {
        return {
          handleExpand: handleDisplayJobDetails,
          showInspectionResults: true,
        };
      },
    }),
    jobNumberWithOutResults: new ColumnDefinition({
      field: JOB_FIELDS.JOB_NUMBER,
      sortable: true,
      lockPinned: true,
      filter: 'agNumberColumnFilter',
      pinned: 'left',
      checkboxSelection: true,
      cellRenderer: cellRendererName,
      cellRendererParams: (params) => {
        return {
          handleExpand: handleDisplayJobDetails,
          showInspectionResults: false,
        };
      },
    }),
    duplicateOf: new ColumnDefinition ({
      field: JOB_FIELDS.DUPLICATE_OF,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueGetter: (({data}) => data?.[JOB_FIELDS.DUPLICATE_OF.api_name]?.[JOB_FIELDS.JOB_NUMBER.api_name])
    }),
    jobType: new ColumnDefinition({
      field: JOB_FIELDS.JOB_TYPE,
      sortable: true,
      filter: 'agSetColumnFilter',
      filterValues: Object.values(allConstants.JOB_TYPES).map((s) => s.value).filter((s) => s !== allConstants.JOB_TYPES.COMMERCIAL.value),
    }),
    hoName: new ColumnDefinition({
      field: JOB_FIELDS.GENERAL.HO_NAME,
      sortable: true,
      parent: JOB_FIELDS.GENERAL.HOMEOWNER.api_name,
      valueGetter: (({data})=> data?.[JOB_FIELDS.GENERAL.HOMEOWNER.api_name]?.[JOB_FIELDS.GENERAL.HO_NAME.api_name] || 'N/A')
    }),
    address: new ColumnDefinition({
      field: JOB_FIELDS.GENERAL.JOBSITE_ADDRESS_LINE_1,
      sortable: true,
      headerName: JOB_FIELDS.GENERAL.ADDRESS.label,
      parent: JOB_FIELDS.GENERAL.ADDRESS.api_name,
      valueGetter: (({data}) => {

        return `${data?.[JOB_FIELDS.GENERAL.ADDRESS.api_name]?.line1??""} ${data?.[JOB_FIELDS.GENERAL.ADDRESS.api_name]?.city??""},
               ${data?.[JOB_FIELDS.GENERAL.ADDRESS.api_name]?.state??""}  ${data?.[JOB_FIELDS.GENERAL.ADDRESS.api_name]?.zipCode??""}`;
               
      }),
    }),
    state: new ColumnDefinition({
      field: JOB_FIELDS.GENERAL.JOBSITE_ADDRESS_STATE,
      sortable: true,
      headerName: JOB_FIELDS.GENERAL.JOBSITE_ADDRESS_STATE.label,
      parent: JOB_FIELDS.GENERAL.ADDRESS.api_name,
      valueGetter: (({data}) => {        
        return data?.[JOB_FIELDS.GENERAL.ADDRESS.api_name]?.state ?? "";
      }),
    }),
    hoPhone: new ColumnDefinition({
      field: JOB_FIELDS.GENERAL.HO_PHONE,
      sortable: false,
      parent: JOB_FIELDS.GENERAL.HOMEOWNER.api_name,
      valueGetter: (({data})=> data?.[JOB_FIELDS.GENERAL.HOMEOWNER.api_name]?.[JOB_FIELDS.GENERAL.HO_PHONE.api_name] || 'N/A')
    }),
    assignedCity: new ColumnDefinition({
      parent: allConstants.PERMIT_MODULE_FIELDS.ASSIGNED_CITY.api_name,
      field: allConstants.CITY_FIELDS.NAME,
      sortable: true,
      headerName: 'assigned city'
    }),

    cityType: new ColumnDefinition({
      parent: allConstants.PERMIT_MODULE_FIELDS.ASSIGNED_CITY.api_name,
      field: allConstants.CITY_FIELDS.CITY_TYPE,
      sortable: true,
      headerName: 'city type',
      wrapText: true,
      cellStyle: {
        lineHeight: '14px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
      },
      removeDefaultFilter: true,
    }),
    inspectionStatus: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.INSPECTION_STATUS,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      headerName: 'inspection status',
      cellRenderer: 'statusRenderer',
      filter: 'agSetColumnFilter',
      filterValues: inspectionStatusFilterValues,
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData && inspectionData?.[INSP_FIELDS.INSPECTION_STATUS.api_name]) {
          if ( inspectionData?.[INSP_FIELDS.INSPECTION_STATUS.api_name] === allConstants.INSPECTION_STATUSES.PENDING.value
              && !isContractor(userRole)) {
            if (inspectionData?.[INSP_FIELDS.ACCEPT_AI_CALL.api_name] 
                 && inspectionData?.[INSP_FIELDS.AI_INSPECTION_STATUS.api_name]) {
              if (inspectionData?.[INSP_FIELDS.AI_INSPECTION_STATUS.api_name] === allConstants.AI_INSPECTION_STATUSES.VERIFY.value) {
                return allConstants.INSPECTION_STATUSES.AI_VERIFY.value
              }
              if (inspectionData?.[INSP_FIELDS.AI_INSPECTION_STATUS.api_name] === allConstants.AI_INSPECTION_STATUSES.READY.value) {
                return allConstants.INSPECTION_STATUSES.AI_READY.value
              }
              if (inspectionData?.[INSP_FIELDS.AI_INSPECTION_STATUS.api_name] === allConstants.AI_INSPECTION_STATUSES.CLOSED_OUT.value) {
                return allConstants.INSPECTION_STATUSES.AI_CLOSED_OUT.value
              }
              else {
                return allConstants.INSPECTION_STATUSES.AI_IN_PROGRESS.value
              }
            }
            else {
              return inspectionData?.[INSP_FIELDS.INSPECTION_STATUS.api_name]
            } 
          }
          else {
              return inspectionData?.[INSP_FIELDS.INSPECTION_STATUS.api_name];
          }
        }
        return allConstants.PERMIT_STATUSES.NOT_REQUIRED.value;
      }),
    }),
    aiCallId: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.AI_CALL_ID,
      hide: true
    }),
    aiStatus: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.AI_INSPECTION_STATUS,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      headerName: 'AI Call Status',
      cellRenderer: 'statusRenderer',
      filter: 'agSetColumnFilter',
      filterValues: Object.values(allConstants.AI_INSPECTION_STATUSES).map((s) => s.value),
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData && inspectionData?.[INSP_FIELDS.AI_INSPECTION_STATUS.api_name]) {
          return inspectionData[INSP_FIELDS.AI_INSPECTION_STATUS.api_name];
        }
        return allConstants.AI_INSPECTION_STATUSES.NOT_ALLOWED.value;
      }),
    }),
    aiCallPriority: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.AI_CALL_PRIORITY,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      headerName: 'AI Call Priority',
      cellRenderer: 'statusRenderer',
      filter: 'agSetColumnFilter',
      filterValues: Object.values(allConstants.AI_CALL_PRIORITIES).map((s) => s.display),
      convertSetFilterValuesToApi: convertAiCallPriorityFilterToApi,
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        return formatAiCallPriorityCell(inspectionData?.[INSP_FIELDS.AI_CALL_PRIORITY.api_name]);
      }),
    }),
    acceptAiCall: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.ACCEPT_AI_CALL,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      headerName: 'Accepted AI Call',
      convertToBoolean: true,
      filter: 'agSetColumnFilter',
      filterValues: ['Yes', 'No'],
      cellRenderer: (params) => (params.value ? 'Yes' : 'No'),
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        const v = inspectionData?.[INSP_FIELDS.ACCEPT_AI_CALL.api_name];
        return !!v;
      }),
    }),
    aiCallAnswer: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.AI_CALL_ANSWER,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      headerName: 'AI Call Answered',
      filter: 'agSetColumnFilter',
      filterValues: ['Yes', 'No'],
    }),
    aiCallInProgress: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.AI_CALL_IN_PROGRESS,
      headerName: 'On Call',
      sortable: true,
      width: 90,
      cellRenderer: 'aiCallInProgressRenderer',
      // Filterable so the AI views can be narrowed to just the calls happening now.
      filter: 'agSetColumnFilter',
      filterValues: ['Yes', 'No'],
      convertToBoolean: true,
      valueGetter: (({data}) => !!data?.[INSP_FIELDS.AI_CALL_IN_PROGRESS.api_name]),
    }),
    aiResult: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.AI_RESULT,
      sortable: true,
      headerName: 'AI Call Result',
      filter: 'agSetColumnFilter',
      filterValues: Object.values(AI_CALL_RESULTS).map((r) => r.display),
      valueGetter: (({data}) => {
        const raw =
          data?.[INSP_FIELDS.INSPECTION.api_name]?.[INSP_FIELDS.AI_RESULT.api_name] ??
          data?.[INSP_FIELDS.AI_RESULT.api_name];
        return formatAiResultCell(raw);
      }),
    }),
    aiAnswer: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.AI_ANSWER,
      sortable: true,
      headerName: 'AI Answer',
    }),
    aiCallDate: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.AI_CALL_DATE,
      parent: INSP_FIELDS.INSPECTION.api_name,
      sortable: true,
      filter: 'agDateColumnFilter',
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData && inspectionData?.[INSP_FIELDS.AI_CALL_DATE.api_name]) {
          // Mostly a date-only value (UTC midnight); read in Pacific time it would show a day early.
          return Utils.formatIsoDateString(inspectionData?.[INSP_FIELDS.AI_CALL_DATE.api_name], {date: true, calendarDate: true});
        }
        return null;
      })
    }),
    schedReason: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.SCHED_REASON,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      headerName: 'Scheduled Reason',
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData && inspectionData?.[INSP_FIELDS.SCHED_REASON.api_name]) {
          const reason = inspectionData[INSP_FIELDS.SCHED_REASON.api_name];
          if (reason === 'Other') {
            return inspectionData[INSP_FIELDS.SCHED_REASON_CUSTOM_TEXT.api_name] || reason;
          }
          return reason;
        }
        return null;
      })
    }),
    schedReasonCustomText: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.SCHED_REASON_CUSTOM_TEXT,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      headerName: 'Other Reason',
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData && inspectionData?.[INSP_FIELDS.SCHED_REASON_CUSTOM_TEXT.api_name]) {
          return inspectionData?.[INSP_FIELDS.SCHED_REASON_CUSTOM_TEXT.api_name];
        }
        return null;
      })
    }),
    failedCount: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.FAILED_COUNT,
      sortable: false,
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData && inspectionData?.[INSP_FIELDS.FAILED_COUNT.api_name]) {
          return inspectionData?.[INSP_FIELDS.FAILED_COUNT.api_name];
        }
        return null;

      }),
    }),
    schedAssignee: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.SCHED_ASSIGNEE,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      //headerName: 'Sched Assignee',
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData) {
          return inspectionData?.[INSP_FIELDS.SCHED_ASSIGNEE.api_name];
        }
        return null;
      })
    }),
    followUpAssignee: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.FOLLOW_UP_ASSIGNEE,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      //headerName: 'FU Assignee',
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData) {
          return inspectionData?.[INSP_FIELDS.FOLLOW_UP_ASSIGNEE.api_name];
        }
        return null;
      })
    }), 
    tags: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.TAGS_TAG_NAME,
      sortable: false,
      parent: INSP_FIELDS.INSPECTION.api_name,
      headerName: 'Tags',
      //wrapText: true,
      cellStyle: {
        lineHeight: '14px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
      },
      filter: 'agSetColumnFilter',
      filterValues: tagNameList,
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData) {
          const tags = inspectionData?.[INSP_FIELDS.TAGS.api_name];

          if (tags !== undefined && tags.length > 0) {
            const tagNames = tags.map(x => x.tagName);
            return {tagNames, showTag: true};
          }
        }
        return null;
      }),
      valueFormatter: ({value}) => value?.tagNames?.join(', ') ?? '',
      cellRenderer: 'LineBreakCellRenderer',
    }),
    tagsReminderDate: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.TAGS_REMINDER_DATE,
      sortable: false,
      parent: INSP_FIELDS.INSPECTION.api_name,
      headerName: 'Reminder',
      cellStyle: {
        lineHeight: '14px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
      },
      filter: 'agDateColumnFilter',
      //filterValues: tagNameList,
      comparator: (filterDate, cellValue) => {
        const cellDate = new Date(cellValue);
        if (cellDate < filterDate) return -1;
        if (cellDate > filterDate) return 1;
        return 0;
      },
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData) {
          const tags = inspectionData?.[INSP_FIELDS.TAGS.api_name];

          if (tags !== undefined && tags.length > 0) {
            const tagReminderDates = tags.map(x => Utils.formatIsoDateString(x.reminderDate, {date: true}));
            return {tagNames: tagReminderDates, showTag: false};
          }
        }
        return null;
      }),
      valueFormatter: ({value}) => value?.tagNames?.join(', ') ?? '',
      cellRenderer: 'LineBreakCellRenderer',
    }),
    inspectionStatusChangeDate: new ColumnDefinition({
      field: allConstants.INSPECTION_FIELDS.INSPECTION_STATUS_CHANGE_DATE,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      //headerName: 'Insp Status Changed',
      filter: 'agDateColumnFilter',
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData) {
          return Utils.formatIsoDateString(inspectionData?.[INSP_FIELDS.INSPECTION_STATUS_CHANGE_DATE.api_name], {date: true});
        }
        return null;
      })
    }),
    inspectionScheduledDate: new ColumnDefinition({
      field: INSP_FIELDS.INSPECTION_SCHEDULED_DATE,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      headerName: 'scheduled date',
      filter: 'agDateColumnFilter',
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData) {

          //console.log('inspection');
          //console.log(data);

          //if (inspectionData?.[INSP_FIELDS.INSPECTION_STATUS.api_name] === "Completed") {
          //  return Utils.formatIsoDateString(inspectionData?.[SCHEDULING_FIELDS.LAST_SCHED_DATE.api_name], {date: true});            
          //}
          //else {
            return Utils.formatIsoDateString(inspectionData?.[SCHEDULING_FIELDS.START_DATE.api_name], {date: true});
          //}
        }
        return null;

      })
    }),
    inspectionResult: new ColumnDefinition({
      field: INSP_FIELDS.RESULT,
      sortable: false,
      parent: `${INSP_FIELDS.INSPECTION.api_name}.${INSP_FIELDS.INSPECTION_RESULTS.api_name}`,
      headerName: 'result',
      cellRenderer: 'statusRenderer',
      filter: 'agSetColumnFilter',
      width: 120,
      filterValues: Object.values(allConstants.RESULT_OPTIONS).map((s) => s.value),
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData) {
          const tests = inspectionData?.[INSP_FIELDS.INSPECTION_RESULTS.api_name];
          if (!tests || tests.length === 0) {
            return null;
          }
          const lastTest = tests[tests.length - 1];
          return lastTest[INSP_FIELDS.RESULT.api_name];
        }
        return null;

      })
    }),
    lastHoCallDate: new ColumnDefinition({
      field: allConstants.CALL_LOGS_FIELDS.LAST_HO_CALL_DATE,
      sortable: false,
      parent: INSP_FIELDS.INSPECTION.api_name,
      filter: 'agDateColumnFilter',
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData) {
          return Utils.formatIsoDateString(inspectionData?.[allConstants.CALL_LOGS_FIELDS.LAST_HO_CALL_DATE.api_name], {date: true});
        }
        return null;
      })
    }),
    lastCallDate: new ColumnDefinition({
      field: allConstants.CALL_LOGS_FIELDS.LAST_CALL_DATE,
      sortable: false,
      parent: INSP_FIELDS.INSPECTION.api_name,
      filter: 'agDateColumnFilter',
      valueGetter: (({data}) => {
        const callsData = data?.[INSP_FIELDS.INSPECTION.api_name]?.[allConstants.CALL_LOGS_FIELDS.CALL_LOG.api_name]
        if(callsData && callsData.length > 0) {
          const callTo = callsData[callsData.length-1];
          return Utils.formatIsoDateString(callTo[allConstants.CALL_LOGS_FIELDS.DATE.api_name], {date: true});
        }
        return '';
      }),
    }),
    lastInspNoteDate: new ColumnDefinition({
      field: INSP_FIELDS.LAST_INSP_NOTE_DATE,
      sortable: false,
      parent: INSP_FIELDS.INSPECTION.api_name,
      filter: 'agDateColumnFilter',
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData) {
          return Utils.formatIsoDateString(inspectionData?.[INSP_FIELDS.LAST_INSP_NOTE_DATE.api_name], {date: true});
        }
        return null;
      }),
    }),
    lastInspNote: new ColumnDefinition({
      field: INSP_FIELDS.LAST_INSP_NOTE,
      sortable: false,
      parent: INSP_FIELDS.INSPECTION.api_name,
      width: 200,
    }),
    lastInspNoteCreatedBy: new ColumnDefinition({
      field: INSP_FIELDS.LAST_INSP_NOTE_CREATED_BY,
      sortable: false,
      parent: INSP_FIELDS.INSPECTION.api_name,
    }),

    // callsCount: new ColumnDefinition({
    //   field: allConstants.CALL_LOGS_FIELDS.NUMBER_OF_CALLS,
    //   sortable: false,
    //   parent: INSP_FIELDS.INSPECTION.api_name,
    //   filter: 'agNumberColumnFilter',
    //   filterParams: numberFilterParams,
    //   valueGetter: (({data}) => {
    //     const callsData = data?.[INSP_FIELDS.INSPECTION.api_name]?.[allConstants.CALL_LOGS_FIELDS.CALL_LOG.api_name];
    //     if(callsData && callsData.length > 0) {
    //       //filter calls to home owner
    //       const callsToHomeOwner = callsData.filter(call => call[allConstants.CALL_LOGS_FIELDS.TYPE.api_name] === allConstants.CALL_TO_OPTIONS.HOME_OWNER.value);
    //       return callsToHomeOwner.length ?? '';
    //     }
    //     return '';
    //   }
    //   ),
    // }),
    callHoCount: new ColumnDefinition({
      field: allConstants.CALL_LOGS_FIELDS.CALL_HO_COUNT,
      sortable: false,
      parent: INSP_FIELDS.INSPECTION.api_name,
      //filter: 'numberRangeFilter',
      filter: 'agNumberColumnFilter',
      //filterParams: numberRangeFilter,
    }),
    // needsLadder: new ColumnDefinition({
    //   field: INSP_FIELDS.LADDER,
    //   sortable: false,
    //   parent: INSP_FIELDS.INSPECTION.api_name,
    //   //filter: 'numberRangeFilter',
    //   filter: 'agBooleanColumnFilter',
    //   //filterParams: numberRangeFilter,
    //   cellRenderer: 'booleanCellRenderer'
    // }),
    requirements: new ColumnDefinition({
      field: INSP_FIELDS.REQUIREMENTS,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      filter: 'requirementFilter',
      //filter: 'agSetColumnFilter',
      filterValues: Object.values(INSPECTION_REQUIREMENTS).map((s) => s.value),
      //filterParams: ["Standard"],
      //filterFramework: RequirementFilter,
      valueGetter: (({data}) => {
        const inspectionData = data?.[INSP_FIELDS.INSPECTION.api_name];
        if (inspectionData) {
          return {
            [INSP_FIELDS.LADDER.api_name]: inspectionData[INSP_FIELDS.LADDER.api_name],
            [INSP_FIELDS.REQUIREMENTS.api_name]: inspectionData?.[INSP_FIELDS.REQUIREMENTS.api_name],
          };
        }
        return null;
      }),
      valueFormatter: ({value}) => {
        if (!value) return '';
        const parts = [];
        if (value[INSP_FIELDS.LADDER.api_name]) parts.push('Ladder');
        if (value[INSP_FIELDS.REQUIREMENTS.api_name]) parts.push(value[INSP_FIELDS.REQUIREMENTS.api_name]);
        return parts.join(', ');
      },
      cellRenderer: 'InspectionRequirementsRenderer',

    }),
    createdTime: new ColumnDefinition({
      field: JOB_FIELDS.CREATE_TIME,
      sortable: true,
      parent: INSP_FIELDS.INSPECTION.api_name,
      filter: 'agDateColumnFilter',
      valueGetter: (({data}) => Utils.formatIsoDateString(data?.[INSP_FIELDS.INSPECTION.api_name]?.[JOB_FIELDS.CREATE_TIME.api_name],{date: true}))
    }),
    ...getStatusColumns(jobOptions)
  };
};

export {getInspectionColumns, inspectionApiTypes};
